import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import cx from 'classnames';

import env from 'lib/env';
import User from 'models/user';
import Brand from 'models/brand';

import { pageProps } from 'lib/analytics/page-properties';
import { BUTTON_IMPRESSION, BUTTON_CLICK } from 'lib/analytics/events';

const { ADMIN_HOST, PORTAL_URL } = env();

const propTypes = {
  brand: PropTypes.instanceOf(Brand).isRequired,
  user: PropTypes.instanceOf(User).isRequired,
  app: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,
};

const defaultProps = {

};

class AccountNavigation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      renderEnterpriseURL: false,
      businessAdminURL: this.props.user.businessAdminURL,
    };

    this.onNavItemVisibilityChange = this.onNavItemVisibilityChange.bind(this);
    this.onNavItemClick = this.onNavItemClick.bind(this);
  }

  componentDidMount() {
    // This prevents client-server mismatches in the enterprise
    // link because the time will be slightly different on the
    // server and client.
    //
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      renderBusinessURL: true,
    });
  }

  onNavItemVisibilityChange(isVisible, name) {
    const { trackEvent, app } = this.props;
    if (isVisible) {
      const pageProperties = pageProps({ app });
      trackEvent({
        ...BUTTON_IMPRESSION,
        ...pageProperties,
        name,
        position: '0',
      });
    }
  }

  onNavItemClick(name) {
    const { trackEvent, app } = this.props;
    const pageProperties = pageProps({ app });
    trackEvent({
      ...BUTTON_CLICK,
      ...pageProperties,
      name,
      position: '0',
    });
  }

  render() {
    const { app, brand, user } = this.props;

    const isBookings = app === 'Bookings';
    const isSettings = app === 'AccountSettings';
    const isPayments = app === 'PaymentMethods';
    const isVehicles = app === 'Vehicles';

    const commonLinkClasses = 'padding-vertical-10 padding-horizontal-20 background-color-hover-blue text-color-dark-slate text-color-hover-white display-block';
    const bookingsLinkClasses = cx({
      'background-color-blue': isBookings,
      'text-color-white': isBookings,
    });
    const settingsLinkClasses = cx({
      'background-color-blue': isSettings,
      'text-color-white': isSettings,
    });
    const paymentsLinkClasses = cx({
      'background-color-blue': isPayments,
      'text-color-white': isPayments,
    });
    const vehiclesLinkClasses = cx({
      'background-color-blue': isVehicles,
      'text-color-white': isVehicles,
    });

    let bookingSection;
    if (isBookings) {
      const subLinkClasses = 'padding-vertical-10 padding-horizontal-20 text-color-blue text-color-hover-blue display-block';
      bookingSection = (
        <div className="background-color-blue-light-grey">
          { user.bookingCount > 0 &&
            <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'Passes'); }} partialVisibility>
              <a className={subLinkClasses} href="#active-bookings" onClick={() => { this.onNavItemClick('Passes'); }}>
                MY PARKING PASSES
              </a>
            </VisibilitySensor>
          }
          <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'FrequentLocations'); }} partialVisibility>
            <a className={subLinkClasses} href="#frequent-locations" onClick={() => { this.onNavItemClick('FrequentLocations'); }}>
              FREQUENT LOCATIONS
            </a>
          </VisibilitySensor>
          <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'ParkingHistory'); }} partialVisibility>
            <a className={subLinkClasses} href="#past-bookings" onClick={() => { this.onNavItemClick('ParkingHistory'); }}>
              PARKING HISTORY
            </a>
          </VisibilitySensor>
        </div>
      );
    }

    return (
      <div className="shadowed margin-bottom-20">
        <div className="background-color-blue-light-grey padding-20 text-color-dark-slate">
          <div className="text-size-16 text-weight-black margin-bottom-5">{user.name}</div>
          <div className="text-size-12">{user.email}</div>
        </div>

        <div className="list-unstyled text-size-12 background-color-white padding-vertical-10">
          { user.admin &&
            <a className={commonLinkClasses} href={ADMIN_HOST}>
              PW ADMIN
            </a>
          }

          { user.isBusinessAdmin &&
            <a className={commonLinkClasses} href={this.state.renderBusinessURL ? user.businessAdminURL : null}>
              BUSINESS ADMIN
            </a>
          }

          { user.affiliate &&
            <a className={commonLinkClasses} href={PORTAL_URL}>
              AFFILIATE CONSOLE
            </a>
          }

          { user.seller &&
            <a className={commonLinkClasses} href={ADMIN_HOST}>
              SELLER CONSOLE
            </a>
          }

          { user.validated &&
            <a className={commonLinkClasses} href="/validation/">
              PARKING VALIDATION
            </a>
          }

          <Link
            className={`${commonLinkClasses} ${bookingsLinkClasses}`}
            to={{ pathname: '/account/' }}
          >
            MY PARKING
            <span className="iconified-font iconified-thin-chevron-down text-color-white pull-right" />
          </Link>
          { bookingSection }

          { user.privatePricingCount > 0 &&
            <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'VipParking'); }} partialVisibility>
              <a className={commonLinkClasses} href="/account/vip/" onClick={() => { this.onNavItemClick('VipParking'); }}>
                VIP PARKING
              </a>
            </VisibilitySensor>
          }

          <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'AccountSettings'); }} partialVisibility>
            <Link
              className={`${commonLinkClasses} ${settingsLinkClasses}`}
              to={{ pathname: '/account/settings/' }}
              onClick={() => { this.onNavItemClick('AccountSettings'); }}
            >
              ACCOUNT SETTINGS
            </Link>
          </VisibilitySensor>

          <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'PaymentMethods'); }} partialVisibility>
            <Link
              className={`${commonLinkClasses} ${paymentsLinkClasses}`}
              to={{ pathname: '/account/payment-methods/' }}
              onClick={() => { this.onNavItemClick('PaymentMethods'); }}
            >
              PAYMENT METHODS
            </Link>
          </VisibilitySensor>

          <VisibilitySensor onChange={(v) => { this.onNavItemVisibilityChange(v, 'SavedVehicles'); }} partialVisibility>
            <Link
              className={`${commonLinkClasses} ${vehiclesLinkClasses}`}
              to={{ pathname: '/account/vehicles/' }}
              onClick={() => { this.onNavItemClick('SavedVehicles'); }}
            >
              SAVED VEHICLES
            </Link>
          </VisibilitySensor>

          { brand.showReferralProgram &&
            <a className={`${commonLinkClasses} squatchpop`} href="#">
              INVITE A FRIEND
            </a>
          }
        </div>
      </div>
    );
  }
}

AccountNavigation.propTypes = propTypes;
AccountNavigation.defaultProps = defaultProps;

export default AccountNavigation;
